import * as Linking from "expo-linking";
import {
  getPathFromState,
  type LinkingOptions,
  type NavigationState,
} from "@react-navigation/native";

// NOTE:
// - Deep linking can only reliably pass primitives (string/number/boolean) via path/query.
// - Screens that require complex objects (e.g. LayoutNodeFilter) are intentionally NOT mapped.

const HIDDEN_FROM_URL = new Set([
  "SalesListFilterRootScreen",
  "StockCountFilterRootScreen",
  "InvoiceArrivalFilterRootScreen",
  "StockOrderFilterRootScreen",
  "ProductListFilterRootScreen",
  "CheckoutRootScreen",
  "AddGiftCardProductCartRootScreen",
]);

// These are *deep-linkable roots*, but we don't want any child screens added to the URL.
const TERMINAL_IN_URL = new Set([
  "ExportModalRootScreen",
  "ReceiptsModalRootScreen",
  "SalesListFilterRootScreen",
  "ProductActionsRootScreen"
]);

export function removeHiddenFromState(state: NavigationState): NavigationState {
  const routes = state.routes.map((r) => {
    // recurse
    const next: any = {
      ...r,
      state: r.state
        ? removeHiddenFromState(r.state as NavigationState)
        : undefined,
    };

    // key change: don't serialize anything underneath these routes into the URL
    if (TERMINAL_IN_URL.has(next.name)) {
      next.state = undefined;
    }

    return next;
  });

  let index = state.index ?? routes.length - 1;

  // remove hidden focused route(s)
  while (
    routes.length > 1 &&
    index >= 0 &&
    HIDDEN_FROM_URL.has(routes[index].name)
  ) {
    routes.splice(index, 1);
    index = Math.min(index - 1, routes.length - 1);
  }

  // drop anything above focused index (stack/modals)
  routes.splice(index + 1);

  return { ...state, routes, index } as NavigationState;
}

export const linking = {
  prefixes: [Linking.createURL("/")],
  config: {
    screens: {
      LogInScreen: "login",
      EurekaOAuthCallbackScreen: "auth/callback/eureka",
      // ========== AUTHORIZED ROOT ==========
      SetupRootScreen: {
        path: "setup",
        screens: {
          SetupSelectLocationScreen: "location",
          SetupSelectTerminalScreen: "terminal",
        },
      },

      OnboardingRootScreen: "onboarding",

      SwitchUserRootScreen: {
        path: "switch-user",
        screens: {
          SwitchUserScreen: "",
          SwitchUserEnterCredentialsScreen: "credentials",
          CreateStaffUserAccountScreen: "staff/create",
          EditStaffUserAccountScreen: "staff/:staffId",
        },
      },

      // ========== APP ROOT (valid setup) ==========
      AppRootScreen: {
        path: "app",
        screens: {
          // ----- Register -----
          RegisterRootScreen: {
            path: "register",
            screens: {
              SellScreen: {
                path: "",
                // editLayout comes through as query (?editLayout=true)
                parse: {
                  editLayout: (v: string) => v === "true" || v === "1",
                },
              },

              // Not mapped — requires productId from active sale context
              // AddGiftCardProductCartRootScreen: { path: "gift-cards", ... }

              MobileCartScreen: "cart",

              CartItemModalRootScreen: {
                path: "cart/item/:lineItemId",
                screens: {
                  EditCartItemModalScreen: "details",
                  DiscountCartItemModalScreen: "discount",
                  EditSerialNumberScreen: "serial-number",
                  EditCartItemNoteScreen: "customer-note",
                  EditProductRootScreen: {
                    path: "product/edit",
                    screens: {
                      EditProductScreen: "",
                    },
                  },
                },
              },

              ProductScannerScreen: {
                path: "scanner",
                screens: {
                  BarcodeScannerScreen: "",
                  BarcodeSettingsScreen: "settings",
                  ScanAgainScreen: "again/:barcode",
                  ProductRootPartialScreen: {
                    path: "product",
                    screens: {
                      ProductDetailScreen: "detail/:productId",
                      ProductPreviewScreen: "preview/:productId",
                    },
                  },
                },
              },

              SerialNumberRootScreen: {
                path: "serial-numbers",
                screens: {
                  AddSerialNumberProductScreen: "add/:productId",
                  ListProductSerialNumbersScreen: "list/:productId",
                  EditSerialNumberScreen: "edit/:itemId",
                },
              },

              CheckoutRootScreen: {
                path: "checkout",
                screens: {
                  /*
                  SelectPaymentMethodScreen: "",
                  CheckoutPaymentHistoryScreen: "history",
                  SplitPaymentScreen: "split",

                  CheckoutCashBackScreen: {
                    path: "cashback/:paymentMethodId",
                    parse: {
                      changeAmount: Number,
                      paymentAmount: Number,
                    },
                  },

                  CheckoutLaybyRootScreen: {
                    path: "layby",
                    screens: {
                      CheckoutLaybyScreen: "",
                      PaymentRootScreen: {
                        path: "pay/:paymentMethodId",
                        parse: {
                          tenderedAmount: Number,
                          minTenderAmount: Number,
                        },
                        screens: {
                          PaymentScreen: "",
                          GiveChangeBackScreen: {
                            path: "change",
                            parse: {
                              changeAmount: Number,
                              paymentAmount: Number,
                            },
                          },
                          GiftCardPaymentRootScreen: {
                            path: "gift-card",
                            screens: {
                              SearchGiftCardScreen: "",
                              GiftCardDetailsScreen: "details/:giftCardNumber",
                              GiftCardPaymentScreen: {
                                path: "pay/:giftCardNumber",
                                parse: {
                                  giftCardBalance: Number,
                                  maximumTenderAmount: Number,
                                },
                              },
                            },
                          },
                        },
                      },
                    },
                  },

                  CheckoutInvoiceRootScreen: {
                    path: "invoice",
                    screens: {
                      CheckoutInvoiceScreen: "",
                    },
                  },

                  PaymentRootScreen: {
                    path: "pay/:paymentMethodId",
                    parse: {
                      tenderedAmount: Number,
                      minTenderAmount: Number,
                    },
                    screens: {
                      PaymentScreen: "",
                      GiveChangeBackScreen: {
                        path: "change",
                        parse: { changeAmount: Number, paymentAmount: Number },
                      },
                      GiftCardPaymentRootScreen: {
                        path: "gift-card",
                        screens: {
                          SearchGiftCardScreen: "",
                          GiftCardDetailsScreen: "details/:giftCardNumber",
                          GiftCardPaymentScreen: {
                            path: "pay/:giftCardNumber",
                            parse: {
                              giftCardBalance: Number,
                              maximumTenderAmount: Number,
                            },
                          },
                        },
                      },
                    },
                  },

                  CheckoutSummaryRootScreen: {
                    path: "summary",
                    screens: {
                      CheckoutReviewScreen: "",
                      CheckoutEmailReceiptScreen: "email",
                      CustomerRootScreen: {
                        path: "customers",
                        screens: {
                          CustomerRootScreen: "",
                          CustomerListScreen: "list",
                          CreateCustomerScreen: "create",
                          EditCustomerScreen: "edit/:customerId",
                        },
                      },
                      CheckoutSummaryMoreActionsRootScreen: {
                        path: "more",
                        screens: {
                          CheckoutSummaryMoreActionsScreen: "",
                          EditPurchaseOrderNumberScreen: "po-number",
                        },
                      },
                    },
                  },
                        */
                },
              },

              ActionsRootScreen: {
                path: "actions",
                screens: {
                  MoreActionsScreen: "",
                  SwitchSaleScreen: "switch-sale",
                  EditSaleNoteScreen: "sale-note",
                  DiscountSaleScreen: "discount",
                  CancelSaleScreen: "cancel",
                  ParkScreen: "park",
                  TransferSelfCheckoutSaleScreen: "transfer/self-checkout",
                  TransferSaleRetailerScreen: "transfer/retailer",
                  PartialConfirmTransferWebSaleScreen: "transfer/web/:saleId",
                  EditPurchaseOrderNumberScreen: "po-number",

                  CustomerRootScreen: {
                    path: "customers",
                    screens: {
                      CustomerRootScreen: "",
                      CustomerListScreen: "list",
                      CreateCustomerScreen: "create",
                      EditCustomerScreen: "edit/:customerId",
                      CustomerListFilterRootScreen: {
                        path: "filters",
                        screens: {
                          CustomerListFilterScreen: "",
                          GenericFilterListScreen: "generic",
                        },
                      },
                    },
                  },

                  AddGiftVoucherRootScreen: {
                    path: "gift-voucher",
                    screens: {
                      AddGiftVoucherScreen: "",
                    },
                  },

                  AddNonMatchedProductScreen: "non-matched/:barcode",

                  ActionCreateQuoteSaleRootScreen: {
                    path: "quote",
                    screens: {
                      CustomerRootScreen: {
                        path: "customers",
                        screens: {
                          CustomerListScreen: "list",
                          CreateCustomerScreen: "create",
                          EditCustomerScreen: "edit/:customerId",
                        },
                      },
                      ConfirmQuoteDetailsScreen: "confirm/:customerId",
                    },
                  },

                  ActionCreateInvoiceSaleRootScreen: {
                    path: "invoice",
                    screens: {
                      CustomerRootScreen: {
                        path: "customers",
                        screens: {
                          CustomerListScreen: "list",
                          CreateCustomerScreen: "create",
                          EditCustomerScreen: "edit/:customerId",
                        },
                      },
                      ConfirmInvoiceDetailsScreen: "confirm/:customerId",
                    },
                  },

                  ActionCreateProformaSaleRootScreen: {
                    path: "proforma",
                    screens: {
                      ListProformasScreen: "",
                      ViewProformaScreen: "view/:proformaId",
                    },
                  },
                },
              },

              // Not mapped (complex object param):
              // ProductFilteredListScreen: { filter: LayoutNodeFilter }
            },
          },

          // ----- Sales -----
          SalesRootScreen: {
            path: "sales",
            screens: {
              SalesListScreen: "",
              /*
              SalesListFilterRootScreen: {
                path: "filters",
                screens: {
                  SalesListFilterScreen: "",
                  SelectableProductsFilterScreen: "products",
                  SelectableCustomersFilterScreen: "customers",
                  GenericFilterListScreen: "generic",
                },
              },
              */
              SaleScreen: "view/:saleId",
              ViewProformaScreen: "proforma/:saleId",
              ReceiptsModalRootScreen: "view/:saleId/receipts",
              ExportModalRootScreen: "view/:saleId/export",
              GenericFilterListScreen: "generic-filter",
              ProductAiInsightsRootModalScreen: "product-ai/:productId",
              AiInsightsRootModalScreen: "ai",
            },
          },

          // ----- Stock Counts -----
          StockCountsRootScreen: {
            path: "stock-counts",
            screens: {
              StockCountsScreen: "",
              StockCountsScreenOffline: "offline",
              /*
              StockCountFilterRootScreen: {
                path: "filters",
                screens: {
                  StockCountFilterScreen: "",
                  GenericFilterListScreen: "generic",
                },
              },
              */

              StockCountCompletedRootScreen: {
                path: "completed/:stockCountId",
                screens: {
                  StockCountCompletedScreen: "",
                  StockCountCompletedCountHistoryScreen: "history/:lineId",
                },
              },
              PerformStockCountRootScreen: {
                path: "perform/:stockCountId",
                screens: {
                  StockCountScreen: "",
                  StockCountScannerScreen: {
                    path: "scanner",
                    screens: {
                      BarcodeScannerScreen: "",
                      ScanAgainScreen: "again/:barcode",
                    },
                  },
                  StockCountActionsRootScreen: {
                    path: "actions",
                    screens: {
                      StockCountActionsScreen: ":stockCountId",
                      ScanModeSelectScreen: "scan-mode",
                    },
                  },
                  StockCountItemHistoryRootScreen: {
                    path: "line/:stockCountLineId",
                    screens: {
                      StockCountItemHistoryScreen: "",
                      EditProductRootScreen: "product/:productId",
                    },
                  },
                  CountProductModalRootScreen: {
                    path: "count",
                    screens: {
                      CountProductModalScreen: "product",
                      CountNonProductModalScreen: "non-product",
                      // CreateProductRootScreen not mapped (form object defaults)
                    },
                  },
                  PermissionsModalRootScreen: {
                    path: "permissions",
                    screens: {
                      CameraPermissionScreen: "camera",
                    },
                  },
                },
              },
              StartStockCountRootScreen: {
                path: "start",
                screens: {
                  StartStockCountScreen: "",
                  SelectLocationModalScreen: "location",
                },
              },
            },
          },

          // ----- Shift -----
          ShiftRootScreen: {
            path: "shift",
            screens: {
              ShiftLandingScreen: "",
              StartNewShiftScreen: "start",
              ShiftHistoryScreen: "history",
              OpenShiftScreen: "open",
              ClosedShiftSummaryScreen: "closed/:shiftId",
              CloseShiftRootScreen: {
                path: "close",
                screens: {
                  CloseCountCashScreen: "count/:paymentMethodId?",
                  CloseShiftNoteScreen: "note",
                },
              },
              AiInsightsRootModalScreen: "ai",
            },
          },

          // ----- Invoice Arrivals -----
          InvoiceArrivalsRootScreen: {
            path: "invoice-arrivals",
            screens: {
              InvoiceArrivalsLandingScreen: "",
              /*
              InvoiceArrivalFilterRootScreen: {
                path: "filters",
                screens: {
                  InvoiceArrivalFilterScreen: "",
                  GenericFilterListScreen: "generic",
                  SelectableProductsFilterScreen: "products",
                },
              },
              */
              CreateInvoiceArrivalRootScreen: {
                path: "create",
                screens: {
                  CreateInvoiceArrivalScreen: "",
                  SelectSupplierModalScreen: "supplier",
                  SelectLocationModalScreen: "location",
                },
              },
              EditInvoiceArrivalRootScreen: {
                path: "edit/:invoiceArrivalId",
                screens: {
                  EditInvoiceArrivalDetailsScreen: "",
                  GenericEditFormInputScreen: "field",
                  EditInvoiceLineItemRootScreen: {
                    path: "line/:lineItemId",
                    screens: {
                      EditInvoiceLineItemScreen: "",
                      // CreateProductRootScreen/EditProductRootScreen not mapped (nested complex flows)
                    },
                  },
                },
              },
              ViewInvoiceArrivalRootScreen: {
                path: "view/:invoiceArrivalId",
                screens: {
                  ViewInvoiceArrivalScreen: "",
                },
              },
            },
          },

          // ----- Stock Orders -----
          StockOrderRootScreen: {
            path: "stock-orders",
            screens: {
              StockOrderListScreen: "",
              /*
              StockOrderFilterRootScreen: {
                path: "filters",
                screens: {
                  StockOrderFilterScreen: "",
                  GenericFilterListScreen: "generic",
                  SelectableProductsFilterScreen: "products",
                },
              },
              */
              CreateStockOrderRootScreen: {
                path: "create",
                screens: {
                  CreateStockOrderScreen: "",
                  SelectLocationModalScreen: "location",
                },
              },
              EditStockOrderRootScreen: {
                path: "edit/:stockOrderId",
                screens: {
                  EditStockOrderScreen: "",
                  // modal roots omitted for brevity
                },
              },
              ViewStockOrderRootScreen: {
                path: "view/:stockOrderId",
                screens: {
                  ViewStockOrderScreen: "",
                },
              },
            },
          },

          // ----- Products -----
          ProductsRootScreen: {
            path: "products",
            screens: {
              ProductsListScreen: "",
              /*
              ProductListFilterRootScreen: {
                path: "filters",
                screens: {
                  ProductListFilterScreen: "",
                  GenericFilterListScreen: "generic",
                },
              },
              */
              // CreateProductRootScreen not mapped (defaultValues is a form object)
              EditProductRootScreen: {
                path: "edit/:productId",
                screens: {
                  EditProductScreen: "",
                  ProductViewSerialNumbersScreen: "serial-numbers",
                  ProductViewInventoryLevelsScreen: "inventory",
                  ProductSelectCategoryScreen: "category",
                  ProductSelectDepartmentScreen: "department",
                  ProductSelectTaxGroupScreen: "tax-group",
                  ProductSelectSupplierScreen: "supplier",
                  ProductSelectUomScreen: "uom",
                  ProductActionsRootScreen: {
                    path: "actions",
                    screens: {
                      ProductActionsScreen: "",
                      ProductActionsSelectParentProductScreen: "parent",
                      ProductActionsSelectParentOptionValuesScreen:
                        "parent/:productId/options",
                      ProductActionsCreateChildVariantScreen: "variants/create",
                      ProductActionsCopyProductScreen: "copy",
                    },
                  },
                  ProductAiInsightsRootModalScreen: "ai/:productId",
                },
              },
            },
          },

          // ----- Settings -----
          SettingsRootScreen: {
            path: "settings",
            screens: {
              SettingsLandingScreen: "",
              PosLayoutSettingsScreen: "layout",
              GeneralSettingsScreen: "general",
              HardwareSettingsScreen: "hardware",
              DataSettingsScreen: "data",
              ReceiptSettingsScreen: "receipt",

              TyroSettingsLandingScreen: "tyro",
              TyroLogsScreen: "tyro/logs",
              ResetTyroSimulationScreen: "tyro/reset",
              SetupTyroSettingScreen: "tyro/setup",

              ShiftSettingsRootScreen: {
                path: "shifts",
                screens: {},
              },
              LaybySettingsRootScreen: {
                path: "layby",
                screens: {},
              },
              TerminalSettingsRootScreen: {
                path: "terminal",
                screens: {
                  TerminalSettingsLandingScreen: "",
                  SelectTerminalSettingScreen: "select",
                  SelectTerminalReceiptTemplateScreen: "receipt-template",
                  SelectTerminalPaymentMethodsScreen: "payment-methods",
                  EditTerminalNameScreen: "name",
                },
              },

              ManageLocationsRootScreen: {
                path: "locations",
                screens: {
                  ManageLocationsScreen: "",
                  EditLocationScreen: {
                    path: "edit/:locationId",
                    screens: {
                      LocationFormScreen: "",
                    },
                  },
                },
              },

              ManageTerminalsRootScreen: {
                path: "terminals",
                screens: {
                  ManageTerminalsScreen: "",
                  EditTerminalScreen: {
                    path: "edit/:terminalId",
                    screens: {
                      TerminalFormScreen: "",
                      SelectPaymentMethodIdsScreen: "payment-method-ids",
                    },
                  },
                },
              },

              ManagePaymentMethodsRootScreen: {
                path: "payment-methods",
                screens: {
                  ListPaymentMethodsScreen: "",
                  // CreatePaymentMethodRootScreen not mapped (defaultFormData is complex)
                },
              },

              SetupNative2faRootScreen: {
                path: "2fa",
                screens: {
                  ListNative2faMethodsScreen: "",
                  Native2faMethodSelectScreen: "select",
                  Native2faEnterIdentifierScreen: "identifier",
                  Native2faVerifyMfaCodeScreen: "verify",
                  RemoveNative2faRootScreen: {
                    path: "remove",
                    screens: {
                      RemoveNative2faEnterCredentialsScreen: "credentials",
                      RemoveNative2faVerifyCodeScreen: "verify",
                    },
                  },
                },
              },

              ManageReceiptTemplatesRootScreen: {
                path: "receipt-templates",
                screens: {
                  ManageReceiptTemplatesScreen: "",
                  // form flows omitted
                },
              },

              DocketSettingsRootScreen: {
                path: "dockets",
                screens: {
                  DocketSettingsScreen: "",
                },
              },

              ApiKeyVaultRootScreen: {
                path: "api-keys",
                screens: {
                  ApiKeyVaultListScreen: "",
                  CreateNewApiVaultKeyRootScreen: {
                    path: "create",
                    screens: {
                      CreateNewApiVaultKeyScreen: "",
                      SelectApiKeyTypeScreen: "type",
                    },
                  },
                },
              },

              ManageAdminApiSecretsRootScreen: {
                path: "admin-api-secrets",
                screens: {
                  ManageAdminApiSecretsScreen: "",
                  CreateAdminApiSecretScreen: "create",
                  ViewAdminApiSecretScreen: "view/:secretId",
                },
              },

              ManageLlmStrategiesRootScreen: {
                path: "llm",
                screens: {
                  AcceptAiTermsScreen: "terms",
                  ManageLlmStrategiesLandingScreen: "",
                  ManageQuickWinHandlerScreen: "quick-win/:quickWinHandlerCode",
                  SelectLlmQuickWinStrategyScreen: "select-quick-win",
                  SelectImageQuickWinStrategyScreen: "select-image-quick-win",
                  ViewLlmStrategyRootScreen: {
                    path: "view/:llmStrategyId",
                    screens: {
                      ViewLlmStrategyScreen: "",
                      UpdateLlmModelScreen: "model",
                      GenericUpdateLlmConfigScreen: "config",
                    },
                  },
                  CreateLlmStrategyRootScreen: {
                    path: "create",
                    screens: {
                      CreateLlmStrategyScreen: "",
                      SelectLlmTypeScreen: "type",
                      SelectOpenAiModelScreen: "openai",
                      SelectAnthropicModelScreen: "anthropic",
                      SelectGeminiModelScreen: "gemini",
                      SelectOpenAiDalleModelScreen: "dalle",
                    },
                  },
                },
              },
            },
          },

          // ----- Barcode Scanner (global) -----
          BarcodeScannerRootScreen: {
            path: "barcode",
            screens: {
              BarcodeScannerScreen: "",
              BarcodeSettingsScreen: "settings",
              ScanAgainScreen: "again/:barcode",
              ProductRootPartialScreen: {
                path: "product",
                screens: {
                  ProductDetailScreen: "detail/:productId",
                  ProductPreviewScreen: "preview/:productId",
                },
              },
            },
          },

        },
      },
    },
  },
  getPathFromState(state, options) {
    const cleaned = removeHiddenFromState(state as NavigationState);
    return getPathFromState(cleaned, options);
  },
} satisfies LinkingOptions<any>; // <-- adjust generic with root param list type when available
